// Traduttore centralizzato errori → messaggio utente in italiano.
//
// Regola: l'utente NON deve MAI vedere stringhe grezze (SQL, nomi di
// constraint, codici inglesi, stack, variabili). Ogni superficie d'errore
// (setError/setToast/banner) passa l'errore qui dentro. Se non riconosciuto,
// si ritorna un messaggio generico — mai il testo originale.
//
// ECCEZIONE (T-094): a volte chi lancia un'eccezione *ha già* costruito un
// messaggio italiano specifico e user-safe (es. callEdge in CapturePhotoPage
// genera "Servizio AI sovraccarico. Riprova…" / "Documento illeggibile (manca:
// totale documento)…" / ecc.). In quei casi, far passare l'errore per i
// pattern di traduzione lo ammazza, perché nessun pattern matcha → cade nel
// fallback generico "Si è verificato un errore". Per preservarli:
//   - Lanciare `new UserFacingError("…")` invece di `new Error("…")`, oppure
//   - Aggiungere `(err as any).__userFacing = true` sul plain Error.
// toUserMessage lo riconosce e ritorna `err.message` direttamente.
//
// Safety-net euristico in coda: se il messaggio sembra già un testo italiano
// user-friendly (parole-chiave comuni, lettere accentate), viene preservato
// anche se chi l'ha lanciato non ha messo il marker. Evita regressioni in
// vecchi siti che fanno `throw new Error("messaggio italiano")`.

/**
 * Marker class per messaggi d'errore già pronti per l'utente (italiano,
 * leggibile, non contengono dettagli tecnici). toUserMessage li passa
 * direttamente senza tentare alcuna traduzione.
 */
export class UserFacingError extends Error {
  readonly __userFacing = true as const;
  constructor(message: string) {
    super(message);
    this.name = 'UserFacingError';
  }
}

function isUserFacing(err: unknown): err is { message: string } {
  if (err instanceof UserFacingError) return true;
  if (err && typeof err === 'object' && (err as { __userFacing?: unknown }).__userFacing === true) {
    return typeof (err as { message?: unknown }).message === 'string';
  }
  return false;
}

// Euristica: il testo "sembra" italiano user-friendly. Usato come safety
// net per call site che fanno `throw new Error("messaggio italiano")` senza
// passare per UserFacingError. Match volutamente conservativo: deve avere
// almeno UNA parola-segnale italiana O un accento, e NON deve contenere
// pattern tipici di stringhe tecniche grezze (constraint SQL, stack trace,
// codici snake_case lunghi).
const ITALIAN_USER_HINT = new RegExp(
  String.raw`[àèéìòù]|\b(Riprova|Errore|Documento|Quota|Servizio|Sessione|Connessione|Manca|Seleziona|Imposta|Configurazione|Aggiorna|Carica|Conferma|Annulla|Riavvia|Locale|Foto|File|Lotto|Scadenza|Allergeni|Fornitore|Ricetta|Etichetta|Magazzino|Cliente|Esci|rientra|fornitore|alimento|illeggibile|sovraccarico|esaurita|riprova|riprovare|esisten\w*)\b`,
  'i',
);
const LOOKS_TECHNICAL = /^[a-z_]{6,}$|violates|constraint|psycopg|stack trace|undefined is not|cannot read prop|TypeError:|at\s+\w+\s+\(/i;
function looksLikeItalianUserMessage(raw: string): boolean {
  if (raw.length < 8 || raw.length > 300) return false;
  if (LOOKS_TECHNICAL.test(raw)) return false;
  return ITALIAN_USER_HINT.test(raw);
}

// Codici applicativi sollevati da RPC / edge functions (chiavi stabili,
// volutamente NON localizzate lato server).
const CODE_MESSAGES: Record<string, string> = {
  local_db_connection_changed: 'Ripristino dei dati in corso. Le operazioni salvate restano in coda; la sincronizzazione riprova automaticamente.',
  local_db_recovery_pending: 'Ripristino dei dati in corso. Le operazioni salvate restano in coda; la sincronizzazione riprova automaticamente.',
  supplier_requires_review: 'Il fornitore potrebbe già esistere. Apri la revisione e scegli l’esistente oppure conferma che è diverso.',
  similar_food_requires_review: 'Un alimento simile è già in magazzino. Apri la revisione e verifica l’abbinamento prima di crearne uno nuovo.',
  import_food_identity_changed: 'Un altro import ha appena registrato questo articolo. Riprova per usare il collegamento esistente.',
  document_rematch_failed: 'Non è stato possibile ricontrollare gli abbinamenti. Verifica la connessione e riapri il documento.',
  // bootstrap / tenant / ruoli
  not_authenticated: 'Sessione non valida. Accedi di nuovo.',
  invalid_role: 'Ruolo non valido per questa operazione.',
  not_member_of_tenant: 'Non fai parte di questa attività.',
  business_not_found: 'Locale non trovato.',
  piva_already_used:
    'Questa Partita IVA è già registrata. Se l’attività è tua, accedi invece di crearne una nuova.',
  tenant_name_already_used: 'Esiste già un’attività registrata con questo nome.',
  atomic_rpc_scope_already_pending:
    'L’operazione è già in coda. Attendi la sincronizzazione prima di riprovare.',
  atomic_rpc_scope_conflict_requires_resolution:
    'Il precedente tentativo è stato rifiutato. Connettiti e ripeti l’operazione per aggiornare i dati.',
  atomic_rpc_conflict_resolution_requires_online:
    'Connettiti a Internet per verificare i dati e ripetere l’operazione.',
  // riconciliazione magazzino append-only (T-492)
  stock_reconciliation_authoritative_reservation_unknown:
    'Esiste una prenotazione con quantità o unità non verificabile. Sincronizza e completa o annulla prima la prenotazione.',
  stock_reconciliation_authoritative_reservation:
    'Esistono quantità prenotate per questo prodotto. Completa o annulla prima la prenotazione.',
  stock_reconciliation_authoritative_history_unknown:
    'Esistono movimenti con un locale non verificabile o incoerente. Correggi prima la provenienza del movimento.',
  stock_reconciliation_draft_reservation:
    'Il prodotto è presente in una spedizione ancora in bozza. Completa o annulla prima la spedizione.',
  stock_reconciliation_cross_business_stock:
    'Il prodotto ha ancora giacenza o prenotazioni in un altro locale. Correggi prima quella sede.',
  stock_reconciliation_unscoped_multisite_history:
    'Esistono movimenti storici senza locale assegnato. La riconciliazione automatica resta bloccata per sicurezza.',
  stock_reconciliation_source_has_operational_references:
    'Questa versione dell’app non può ancora unire un prodotto usato in ricette. Aggiorna l’app e riprova.',
  stock_reconciliation_source_not_receive_only:
    'Questa versione dell’app non può ancora unire questo storico di magazzino. Aggiorna l’app e riprova.',
  stock_reconciliation_food_version_conflict:
    'Il prodotto è cambiato durante la correzione. Riapri la procedura e riprova.',
  stock_reconciliation_recipe_reference_conflict:
    'Le ricette collegate sono cambiate. Riapri la procedura e verifica di nuovo.',
  stock_reconciliation_source_snapshot_conflict:
    'La giacenza è cambiata durante la correzione. Sincronizza, riapri la procedura e riprova.',
  stock_reconciliation_incomplete_source_snapshot:
    'La fotografia della giacenza non contiene tutti i lotti. Riapri la procedura e verifica le quantità.',
  stock_reconciliation_archived_requires_zero_snapshot:
    'Il prodotto è archiviato: puoi chiudere lo storico incoerente soltanto confermando quantità zero nella sua unità attuale.',
  // inviti staff (accept-invite edge function)
  invite_not_found: 'Invito non trovato. Chiedi al titolare di rigenerarlo.',
  invite_revoked: 'Questo invito è stato annullato. Chiedine uno nuovo al titolare.',
  invite_already_used: 'Questo invito è già stato utilizzato.',
  invite_expired: 'Invito scaduto. Chiedi al titolare un nuovo invito.',
  invite_email_mismatch:
    'L’email con cui hai effettuato l’accesso non corrisponde a quella dell’invito.',
  operator_pin_not_recoverable:
    'Questo PIN è stato creato prima della funzione “Mostra PIN” e non può essere recuperato. Rigeneralo una volta: il nuovo codice sarà inviato via email e resterà visualizzabile.',
  operator_pin_encryption_key_missing:
    'Il servizio PIN non è disponibile in questo momento. Riprova tra poco.',
};

// Errori Supabase Auth (testi inglesi noti) → italiano.
const AUTH_PATTERNS: Array<[RegExp, string]> = [
  [/invalid login credentials/i, 'Email o password non corretti.'],
  [/email not confirmed/i, 'Account non ancora attivo. Riprova tra poco.'],
  [/user already registered|already.*registered/i, 'Questa email è già registrata. Prova ad accedere.'],
  [/password should be at least|password.*at least/i, 'La password deve avere almeno 8 caratteri.'],
  [/weak password/i, 'Password troppo debole. Usane una più lunga.'],
  [/unable to validate email|invalid.*email/i, 'Indirizzo email non valido.'],
  [/email.*rate limit|over_email_send_rate_limit|too many requests|rate limit/i,
    'Troppi tentativi. Riprova tra qualche minuto.'],
  [/redirect_uri_mismatch|oauth|provider.*not enabled|bad_oauth/i,
    'Accesso con Google non riuscito. Riprova.'],
  [/session.*expired|jwt expired|invalid token|refresh token/i,
    'Sessione scaduta. Accedi di nuovo.'],
];

function rawText(err: unknown): string {
  if (err == null) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message ?? '';
  if (typeof err === 'object') {
    const o = err as Record<string, unknown>;
    return String(
      o.message ?? o.error_description ?? o.error ?? o.msg ?? o.details ?? '',
    );
  }
  return '';
}

/**
 * Converte qualunque errore in un messaggio utente sicuro in italiano.
 * Non restituisce MAI il testo grezzo dell'errore — eccetto quando chi
 * lo lancia ha esplicitamente dichiarato che è già user-safe
 * (UserFacingError o `__userFacing=true`) o l'euristica lo riconosce
 * come messaggio italiano leggibile (vedi T-094).
 */
export function toUserMessage(err: unknown, fallback = 'Si è verificato un errore. Riprova.'): string {
  // 0. Messaggio già pronto per l'utente: passa diretto (T-094).
  if (isUserFacing(err)) return err.message;

  const raw = rawText(err).trim();
  if (!raw) return fallback;

  // 1. Codice applicativo esatto (es. "piva_already_used").
  const key = raw.toLowerCase();
  if (CODE_MESSAGES[key]) return CODE_MESSAGES[key];
  // a volte arriva come "… piva_already_used" dentro un messaggio Postgres.
  for (const code of Object.keys(CODE_MESSAGES)) {
    if (key.includes(code)) return CODE_MESSAGES[code];
  }

  // 2. Violazione di unicità Postgres (mai mostrare il nome del constraint).
  if (/duplicate key value|unique constraint|already exists/i.test(raw)) {
    if (/piva/i.test(raw)) return CODE_MESSAGES.piva_already_used;
    if (/tenants_name|tenant.*name/i.test(raw)) return CODE_MESSAGES.tenant_name_already_used;
    return 'Esiste già un elemento con questi dati.';
  }
  // Altri vincoli DB → generico, mai grezzo.
  if (/violates (foreign key|not-null|check) constraint|null value in column/i.test(raw)) {
    return 'Dati non validi o incompleti. Controlla i campi e riprova.';
  }

  // 3. Rete / offline.
  if (/failed to fetch|networkerror|load failed|network request failed|timeout|ecconnrefused|fetch ?error/i.test(raw)) {
    return 'Connessione assente. Verifica la rete e riprova.';
  }

  // 4. Errori Auth noti.
  for (const [re, msg] of AUTH_PATTERNS) {
    if (re.test(raw)) return msg;
  }

  // 5. Safety-net (T-094): se sembra già un messaggio italiano user-friendly,
  // restituiscilo invece del fallback generico. Copre i call site legacy che
  // fanno `throw new Error("messaggio italiano")` senza UserFacingError.
  if (looksLikeItalianUserMessage(raw)) return raw;

  // 6. Sconosciuto → generico, MAI il testo originale.
  return fallback;
}
